import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchModulesVersions } from 'api/manageModules/manageModulesThunks';
import { WORKFLOW_INITIAL_OBJECT } from 'components/DesignOrchestrationDiagram/DesignOrchestrationDiagram.constans';
import {
  updateWorkflowState,
  assignStepIndexes,
  extractStepIndexByUniqueId,
} from 'components/DesignOrchestrationDiagram/utils/workflowHelper/designOrchestrationWorkflowHelpers';
import { serverModules } from 'dto/designOrchestration/modules/serverModules';
import {
  dropZoneStepTypes,
  IDropZoneWorkflowStep,
} from 'interfaces/designOrchestration/designOrchestrationDiagram.interface';
import {
  IModuleOnDrag,
  IStepModule,
} from 'interfaces/designOrchestration/designOrchestrationModules.interface';
import { IDesignOrchestrationSlice } from 'interfaces/designOrchestration/designOrchestrationState.interface';
import { IModuleServer } from 'interfaces/modules/module.interface';
import { VIEW_OPTIONS } from 'pages/DesignOrchestration/DesignOrchestration.constants';
import { RootState } from 'redux/store';
import { includePropertiesInCleanStep } from 'utils/commonFunctions/CommonFunctions';
import {
  removeModulesFromWorkflow,
  findStepInWorkflow,
  findAllRelatedStepIds,
  filterStepModulesForDeletion,
} from 'components/DesignOrchestrationDiagram/utils/deleteHelpers/designOrchestrationFunctions';
import { Useuid } from 'utils/hooks/useUid';

export const SLICE_KEY = 'designOrchestration';

const initialState: IDesignOrchestrationSlice = {
  serverModules: [],
  isAccordionOpen: true,
  stepModules: [],
  orchestrationDetails: {
    orchestrationName: undefined,
    orchestrationDescription: undefined,
    businessProduct: undefined,
    throttleRate: undefined,
    cronTrigger: undefined,
    cronTimezone: undefined,
    sftpDropzone: undefined,
    sftpPrivateKey: undefined,
    ftpDropzone: undefined,
    s3Dropzone: undefined,
    notificationEmails: undefined,
    eventParamsMapping: undefined,
    reportingIndex: undefined,
    unpackProduct: undefined,
  },
  loading: false,
  xmlCode: '',
  workflowState: WORKFLOW_INITIAL_OBJECT,
  xmlPanelWidth: 0,
  selectedElementId: null,
  selectedElementIds: [],
  selectedElementIdToDelete: null,
  selectedElementIdsToDelete: [],
  deleteTooltipOpenId: '',
  viewState: {
    currentView: VIEW_OPTIONS.COLUMN_VIEW,
    isDetailsOpen: true,
    isModulesOpen: true,
  },
};

const designOrchestrationSlice = createSlice({
  name: SLICE_KEY,
  initialState,
  reducers: {
    clearState: (state) => {
      return initialState;
    },
    setOrchestrationDetails: (
      state,
      action: PayloadAction<
        Partial<IDesignOrchestrationSlice['orchestrationDetails']>
      >
    ) => {
      state.orchestrationDetails = {
        ...state.orchestrationDetails,
        ...action.payload,
      };
    },
    setAccordionStatus: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isAccordionOpen: action.payload,
      };
    },
    setStepAccordionStatus: (
      state,
      action: PayloadAction<{
        order: number;
        moduleName: string;
        moduleVersion: string;
        isOpen: boolean;
      }>
    ) => {
      return {
        ...state,
        stepModules: state.stepModules?.map((stepModule) => {
          if (
            stepModule.order === action.payload?.order &&
            stepModule.textId === action.payload?.moduleName &&
            stepModule.version === action.payload?.moduleVersion
          ) {
            return { ...stepModule, isOpen: action.payload.isOpen };
          }
          return { ...stepModule };
        }),
      };
    },
    setXmlCode: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        xmlCode: action.payload,
      };
    },
    updateStepModuleField: (
      state,
      action: PayloadAction<{
        moduleName: string;
        moduleVersion: string;
        order?: number;
        parameterTextId: string;
        value: string;
        escSpecSymbols?: boolean;
        stepId?: string;
        stepCondition?: string;
        retryNumber?: number;
        retryDelay?: number;
        retryDelayMultiplier?: number;
      }>
    ) => {
      const {
        moduleName,
        moduleVersion,
        order,
        value,
        parameterTextId,
        escSpecSymbols,
        stepId,
        stepCondition,
        retryNumber,
        retryDelay,
        retryDelayMultiplier,
      } = action.payload;
      const moduleIndexToUpdate = state.stepModules.findIndex(
        (module) =>
          module.textId === moduleName &&
          module.version === moduleVersion &&
          module.order === order
      );

      if (state.stepModules[moduleIndexToUpdate]?.textId) {
        return {
          ...state,
          stepModules: state.stepModules.map((stepModule, moduleIndex) => {
            if (moduleIndex === moduleIndexToUpdate) {
              const stepLevelProps: Partial<IStepModule> = {};

              includePropertiesInCleanStep(stepLevelProps, {
                stepId,
                stepCondition,
                retryNumber,
                retryDelay,
                retryDelayMultiplier,
              });

              const updatedStepModule = { ...stepModule, ...stepLevelProps };

              if (parameterTextId) {
                const parameterIndexToUpdate = stepModule.parameters.findIndex(
                  (parameter) => parameter.textId === parameterTextId
                );

                if (parameterIndexToUpdate !== -1) {
                  updatedStepModule.parameters = stepModule.parameters.map(
                    (parameter, parameterIndex) => {
                      if (parameterIndex === parameterIndexToUpdate) {
                        if (escSpecSymbols !== undefined) {
                          return { ...parameter, escSpecSymbols };
                        } else {
                          return { ...parameter, value };
                        }
                      }
                      return { ...parameter };
                    }
                  );
                }
              }

              return updatedStepModule;
            }
            return { ...stepModule };
          }),
        };
      }
      return { ...state };
    },
    setXmlPanelWidth: (state, action: PayloadAction<number>) => {
      state.xmlPanelWidth = action.payload;
    },
    updateViewState: (
      state,
      action: PayloadAction<Partial<IDesignOrchestrationSlice['viewState']>>
    ) => {
      state.viewState = {
        ...state.viewState,
        ...action.payload,
      };
    },
    setSelectedElement: (state, action: PayloadAction<string | null>) => {
      state.selectedElementId = action.payload;
    },
    setSelectedElementsWithRelated: (state, action: PayloadAction<string>) => {
      const stepId = action.payload;
      const [relatedIds] = findAllRelatedStepIds(stepId, state.workflowState);
      state.selectedElementIdToDelete = stepId;
      state.selectedElementIdsToDelete = relatedIds;
    },
    clearSelectedElements: (state) => {
      state.selectedElementId = null;
      state.selectedElementIds = [];
      state.selectedElementIdToDelete = null;
      state.selectedElementIdsToDelete = [];
    },
    setIsDeleteTooltipOpen: (state, action: PayloadAction<string>) => {
      state.deleteTooltipOpenId = action.payload;
    },
    addStepModuleAndUpdateWorkflow: (
      state,
      action: PayloadAction<{
        textId: string;
        moduleVersion: string;
        stepType: dropZoneStepTypes | undefined;
        targetWorkflowStepId: string;
        module: IModuleOnDrag;
        isGlobalFailback: boolean;
        existingNodeIndex?: number;
      }>
    ) => {
      try {
        // Step 1: Find the module to add from serverModules
        const moduleToAdd = state.serverModules.find(
          (module) =>
            module.textId === action.payload?.textId &&
            module.version === action.payload?.moduleVersion
        );

        if (!moduleToAdd) {
          console.error(
            `addStepModuleAndUpdateWorkflow: Module not found in serverModules. textId: ${action.payload.textId}, version: ${action.payload.moduleVersion}`
          );
          return state;
        }

        // Step 2: Generate uniqueId and prepare module with uniqueId
        const uniqueId = Useuid();
        const moduleWithUniqueId = {
          ...action.payload?.module,
          uniqueId,
        };

        // Step 3: Attempt to update the workflow state
        const newWorkflowState = updateWorkflowState(
          action.payload?.targetWorkflowStepId,
          state.workflowState,
          moduleWithUniqueId,
          action.payload?.isGlobalFailback
        );

        // Step 4: Verify that the workflow was actually updated
        if (newWorkflowState === state.workflowState) {
          console.error(
            `addStepModuleAndUpdateWorkflow: Workflow update failed. targetWorkflowStepId: ${action.payload.targetWorkflowStepId}, isGlobalFailback: ${action.payload.isGlobalFailback}`
          );
          return state;
        }

        // Step 5: Assign step indexes to the updated workflow
        const workflowWithIndexes = assignStepIndexes(newWorkflowState);

        // Step 6: Extract step index mapping from the updated workflow
        const stepIndexMap = extractStepIndexByUniqueId(workflowWithIndexes);

        // Step 7: Verify that our new step exists in the workflow with a valid stepIndex
        if (stepIndexMap[uniqueId] === undefined) {
          console.error(
            `addStepModuleAndUpdateWorkflow: Step not found in updated workflow. uniqueId: ${uniqueId}, stepIndexMap:`,
            stepIndexMap
          );
          return state;
        }

        // Step 8: Prepare the new step module with sorted parameters
        const sortedParameters = [...moduleToAdd.parameters]?.sort((a, b) => {
          const aQualified =
            a.type === 'ENUM' &&
            moduleToAdd.parameters?.some(
              (param) => param.dependsId === a.textId
            );
          const bQualified =
            b.type === 'ENUM' &&
            moduleToAdd.parameters?.some(
              (param) => param.dependsId === b.textId
            );

          if (aQualified && !bQualified) {
            return -1;
          }
          if (!aQualified && bQualified) {
            return 1;
          }
          return 0;
        });

        const filteredStepModules = action.payload.existingNodeIndex
          ? state.stepModules.filter(
              ({ stepIndex }) => stepIndex !== action.payload.existingNodeIndex
            )
          : state.stepModules;
        const newStepModule = {
          ...moduleToAdd,
          parameters: sortedParameters,
          order: filteredStepModules.length + 1,
          isOpen: true,
          stepType: action.payload.stepType,
          uniqueId,
          stepIndex: stepIndexMap[uniqueId],
        };

        // Step 9: Update existing stepModules with new stepIndexes
        const updatedExistingStepModules = filteredStepModules.map(
          (stepModule) => {
            if (
              stepModule.uniqueId &&
              stepIndexMap[stepModule.uniqueId] !== undefined
            ) {
              return {
                ...stepModule,
                stepIndex: stepIndexMap[stepModule.uniqueId],
              };
            }
            return stepModule;
          }
        );

        // Step 10: Combine existing and new step modules
        const finalStepModules = [...updatedExistingStepModules, newStepModule];

        // Step 11: Return the updated state with both workflow and stepModules synchronized
        return {
          ...state,
          workflowState: workflowWithIndexes,
          stepModules: finalStepModules,
        };
      } catch (error) {
        console.error(
          'addStepModuleAndUpdateWorkflow: Unexpected error occurred:',
          error,
          {
            payload: action.payload,
            currentStepModulesLength: state.stepModules.length,
            currentWorkflowState: state.workflowState,
          }
        );
        return state; // Return unchanged state on any error
      }
    },
    deleteStepModuleAndUpdateWorkflow: (
      state,
      action: PayloadAction<string>
    ) => {
      try {
        const stepId = action.payload;

        // Find the step in workflow to get its stepIndex and uniqueId
        let targetStep: IDropZoneWorkflowStep | null = null;

        // Search in main workflow
        if (state.workflowState.workflow.nextStepGroup) {
          targetStep = findStepInWorkflow(
            state.workflowState.workflow.nextStepGroup,
            stepId
          );
        }

        // Search in failback workflow if not found in main
        if (!targetStep && state.workflowState.workflow.failbackStepGroup) {
          targetStep = findStepInWorkflow(
            state.workflowState.workflow.failbackStepGroup,
            stepId
          );
        }

        // Step 1: Find all related step IDs that need to be deleted
        const [relatedStepIds, relatedUniqueIds] = findAllRelatedStepIds(
          stepId,
          state.workflowState
        );

        // Step 2: Remove modules from stepModules array
        const updatedStepModules = filterStepModulesForDeletion(
          state.stepModules,
          targetStep,
          relatedStepIds,
          relatedUniqueIds
        );

        // Step 3: Update workflow state by removing the modules
        const newWorkflowState = removeModulesFromWorkflow(
          state.workflowState,
          relatedStepIds
        );

        // Step 4: Reassign step indexes after deletion
        const workflowWithIndexes = assignStepIndexes(newWorkflowState);

        // Step 5: Extract updated step index mapping
        const stepIndexMap = extractStepIndexByUniqueId(workflowWithIndexes);

        // Step 6: Update remaining stepModules with new stepIndexes and renumber orders
        const finalStepModules = updatedStepModules
          .map((stepModule) => {
            if (
              stepModule.uniqueId &&
              stepIndexMap[stepModule.uniqueId] !== undefined
            ) {
              return {
                ...stepModule,
                stepIndex: stepIndexMap[stepModule.uniqueId],
              };
            }
            return stepModule;
          })
          .map((stepModule, index) => ({
            ...stepModule,
            order: index + 1,
          }));

        return {
          ...state,
          workflowState: workflowWithIndexes,
          stepModules: finalStepModules,
          selectedElementId: null,
          selectedElementIds: [],
        };
      } catch (error) {
        console.error(
          'deleteStepModuleAndUpdateWorkflow: Unexpected error occurred:',
          error
        );
        return state;
      }
    },
    draggingStepModuleAndUpdateWorkflow: (
      state,
      action: PayloadAction<string>
    ) => {
      try {
        const stepId = action.payload;

        const [relatedStepIds] = findAllRelatedStepIds(
          stepId,
          state.workflowState
        );

        const newWorkflowState = removeModulesFromWorkflow(
          state.workflowState,
          relatedStepIds
        );

        return {
          ...state,
          workflowState: newWorkflowState,
          stepModules: state.stepModules,
          selectedElementId: null,
          selectedElementIds: [],
        };
      } catch (error) {
        console.error(
          'deleteStepModuleAndUpdateWorkflow: Unexpected error occurred:',
          error
        );
        return state;
      }
    },
    setWorkflowState: (state, action: PayloadAction<any>) => {
      return { ...state, workflowState: action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchModulesVersions.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(fetchModulesVersions.rejected, (state) => {
      return {
        ...state,
        loading: false,
      };
    });
    builder.addCase(
      fetchModulesVersions.fulfilled,
      (state, { payload }: { payload: IModuleServer[] }) => {
        return {
          ...state,
          serverModules: serverModules(payload),
          loading: false,
        };
      }
    );
  },
});

export const selectOrchestrationDetails = (state: RootState) =>
  state.designOrchestration.orchestrationDetails;

export const selectOrchestrationName = (state: RootState) =>
  state.designOrchestration.orchestrationDetails.orchestrationName?.trim() ||
  '';

export const selectAccordionStatus = (state: RootState) =>
  state.designOrchestration.isAccordionOpen;

export const selectModulesVersions = (state: RootState) =>
  state.designOrchestration.serverModules;

export const selectLoading = (state: RootState) =>
  state.designOrchestration.loading;

export const selectXmlCode = (state: RootState) =>
  state.designOrchestration.xmlCode;

export const selectStepModules = (state: RootState) =>
  state.designOrchestration.stepModules;

export const selectStepModulesLength = (state: RootState) =>
  state.designOrchestration.stepModules?.length;

export const selectWorkflowState = (state: RootState) =>
  state.designOrchestration.workflowState;

export const selectXmlPanelWidth = (state: RootState) =>
  state.designOrchestration.xmlPanelWidth;

export const selectViewState = (state: RootState) =>
  state.designOrchestration.viewState;

export const selectSelectedElementIds = (state: RootState) =>
  state.designOrchestration.selectedElementIds;
export const selectSelectedElementIdsToDelete = (state: RootState) =>
  state.designOrchestration.selectedElementIdsToDelete;

export const {
  clearState,
  setOrchestrationDetails,
  setAccordionStatus,
  setXmlCode,
  updateStepModuleField,
  setStepAccordionStatus,
  setXmlPanelWidth,
  updateViewState,
  setSelectedElement,
  setSelectedElementsWithRelated,
  clearSelectedElements,
  addStepModuleAndUpdateWorkflow,
  deleteStepModuleAndUpdateWorkflow,
  setIsDeleteTooltipOpen,
  draggingStepModuleAndUpdateWorkflow,
  setWorkflowState,
} = designOrchestrationSlice.actions;

export default designOrchestrationSlice.reducer;
